/**
 * Compact voice mic + transcript bubble for non-BBP screens.
 *
 * Drop-in: render once per page, pass the result of useVoicePage() as
 * the `voice` prop. Optional `examples` array shows a "Try saying"
 * hint strip with ▶ Listen buttons next to each phrase (uses the
 * existing voiceSamples.js manifest).
 *
 *   const voice = useVoicePage({ parser: pageParser });
 *   <VoicePageMic voice={voice} examples={["Kohli", "Captain Kohli"]} />
 */
import { Mic, MicOff, AlertTriangle, HelpCircle, Volume2, Radio } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import VoicePlayButton from "../ballbyball/VoicePlayButton";
import { useWhisperSTT } from "../../hooks/useWhisperSTT";


export default function VoicePageMic({
  voice,
  examples = null,
  helpTitle = "Voice commands",
  helpBody = null,             // optional ReactNode rendered inside the help modal
  className = "",
}) {
  const { supported, listening, transcript, lastApplied, error, toggle, applyTranscript } = voice;
  const [helpOpen, setHelpOpen] = useState(false);

  // Push-to-talk Whisper. Krishna's team rated browser STT 3/10
  // (2026-05-26); Whisper-via-Groq lifts accuracy to ~95% on Indian
  // English cricket vocab. Renders alongside the continuous mic so
  // the operator can pick which feels better — PTT is the recommended
  // default.
  const ptt = useWhisperSTT({
    onTranscript: (text) => applyTranscript?.(text),
  });

  // Hold "V" anywhere (outside inputs) to record. Press = start;
  // release = stop + transcribe. Replaces the continuous toggle for
  // operators who want the higher-accuracy PTT path.
  useEffect(() => {
    if (!ptt.supported) return undefined;
    let pressed = false;
    const isFormField = (el) => {
      const tag = (el?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return true;
      if (el?.isContentEditable) return true;
      return false;
    };
    const onDown = (e) => {
      if ((e.key || "").toLowerCase() !== "v") return;
      if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return;
      if (e.repeat) return;
      if (isFormField(e.target)) return;
      pressed = true;
      e.preventDefault();
      ptt.startRecording();
    };
    const onUp = (e) => {
      if ((e.key || "").toLowerCase() !== "v") return;
      if (!pressed) return;
      pressed = false;
      e.preventDefault();
      ptt.stopRecording();
    };
    window.addEventListener("keydown", onDown);
    window.addEventListener("keyup", onUp);
    return () => {
      window.removeEventListener("keydown", onDown);
      window.removeEventListener("keyup", onUp);
    };
  }, [ptt.supported, ptt.startRecording, ptt.stopRecording]);

  // Choose between PTT (Whisper) and Continuous (browser STT). PTT
  // wins on accuracy; continuous wins on hands-busy ergonomics. Default
  // to PTT — the founder ask 2026-05-26 ("3/10 accuracy") was the
  // signal to swap modes. Persist per-browser.
  const pttPreferred = (() => {
    try { return localStorage.getItem("zcd_voice_mode") !== "continuous"; }
    catch { return true; }
  })();

  return (
    <div className={`inline-flex flex-wrap items-center gap-2 rounded-md border border-purple-400/40 bg-purple-500/5 px-2 py-1 text-xs ${className}`}>
      {pttPreferred && ptt.supported ? (
        // ── PTT mode — high-accuracy Whisper ─────────────────────────
        <button
          type="button"
          // Unified pointer events + pointer capture — see VoiceScoringPanel.jsx
          // for the rationale (no finger-drift stop, no touch/mouse double-fire).
          onPointerDown={(e) => {
            e.preventDefault();
            try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
            ptt.startRecording();
          }}
          onPointerUp={(e) => {
            try { if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
            ptt.stopRecording();
          }}
          onPointerCancel={(e) => {
            try { if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
            if (ptt.recording) ptt.stopRecording();
          }}
          onContextMenu={(e) => e.preventDefault()}
          aria-pressed={!!ptt.recording}
          title={
            ptt.recording ? "Release to transcribe" :
            ptt.transcribing ? "Transcribing…" :
            "Hold V (or press-and-hold) to record"
          }
          className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md font-semibold uppercase tracking-wider text-[10px] touch-manipulation transition-colors select-none ${
            ptt.recording ? "bg-red-500/30 border border-red-500/60 text-red-200 animate-pulse" :
            ptt.transcribing ? "bg-amber-500/20 border border-amber-500/50 text-amber-300" :
            "bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25"
          }`}
        >
          {ptt.recording ? <Mic className="w-3.5 h-3.5" /> : <Radio className="w-3.5 h-3.5" />}
          {ptt.recording ? "Recording…" : ptt.transcribing ? "Parsing…" : "Hold to talk"}
          <kbd className="hidden sm:inline ml-0.5 text-[9px] font-mono px-1 rounded bg-black/30 border border-current/40 opacity-70">V</kbd>
        </button>
      ) : (
        // ── Continuous mode — legacy browser STT (lower accuracy) ────
        <button
          type="button"
          onClick={toggle}
          disabled={!supported}
          aria-pressed={!!listening}
          title={
            !supported ? "Voice not supported (use Chrome or Edge)" :
            listening ? "Stop listening (V)" : "Start listening (V)"
          }
          className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md font-semibold uppercase tracking-wider text-[10px] touch-manipulation transition-colors ${
            !supported ? "bg-bg-secondary text-text-secondary opacity-50" :
            listening ? "bg-red-500/20 border border-red-500/50 text-red-300 animate-pulse" :
            "bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25"
          }`}
        >
          {listening ? <Mic className="w-3.5 h-3.5" /> : <MicOff className="w-3.5 h-3.5" />}
          {listening ? "Listening…" : "Voice"}
          {supported && (
            <kbd className="hidden sm:inline ml-0.5 text-[9px] font-mono px-1 rounded bg-black/30 border border-current/40 opacity-70">V</kbd>
          )}
        </button>
      )}

      <div className="min-w-[140px] max-w-[280px] truncate text-text-secondary">
        {(ptt.error || error) ? (
          <span className="flex items-center gap-1 text-red-400 truncate" title={ptt.error || error}>
            <AlertTriangle className="w-3 h-3 shrink-0" /> {ptt.error || error}
          </span>
        ) : ptt.recording ? (
          <span className="text-red-300 truncate block">● recording — release V to send</span>
        ) : ptt.transcribing ? (
          <span className="text-amber-300 truncate block">⏳ transcribing audio…</span>
        ) : ptt.lastTranscript ? (
          <span title={ptt.lastTranscript} className="truncate block text-text-primary">
            <span className="text-[9px] uppercase tracking-wider text-text-secondary mr-1">heard:</span>
            {ptt.lastTranscript}
          </span>
        ) : listening ? (
          <span title={transcript || "Say a command…"} className="truncate block">
            {transcript || <em className="opacity-60">say a command…</em>}
          </span>
        ) : lastApplied ? (
          <span title={lastApplied.label} className="truncate block text-text-primary">
            <span className="text-[9px] uppercase tracking-wider text-text-secondary mr-1">last:</span>
            {lastApplied.label}
          </span>
        ) : (
          <span className="opacity-60">hold V to talk</span>
        )}
      </div>

      {examples && examples.length > 0 && (
        <button
          type="button"
          onClick={() => setHelpOpen(true)}
          aria-label="Show voice commands"
          title="Voice command examples"
          className="text-purple-300 hover:text-purple-100"
        >
          <HelpCircle className="w-3.5 h-3.5" />
        </button>
      )}

      {helpOpen && examples && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
          onClick={() => setHelpOpen(false)}
        >
          <div
            className="bg-bg-primary rounded-2xl max-w-md w-full border border-purple-400/40 shadow-2xl p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold flex items-center gap-2 text-purple-200">
                <Volume2 className="w-4 h-4" /> {helpTitle}
              </h3>
              <button onClick={() => setHelpOpen(false)} className="text-text-secondary hover:text-text-primary">✕</button>
            </div>

            {helpBody && (
              <div className="text-[11px] text-text-secondary leading-relaxed mb-3">
                {helpBody}
              </div>
            )}

            <div className="text-[10px] uppercase tracking-wider text-purple-300 font-medium mb-1.5">
              Try saying — tap 🔊 to hear
            </div>
            <div className="flex flex-wrap gap-1.5">
              {examples.map((ex) => (
                <span
                  key={ex}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-bg-secondary/80 border border-purple-400/20 rounded text-[11px] font-mono text-text-primary"
                >
                  "{ex}"
                  <VoicePlayButton phrase={ex} size="xs" />
                </span>
              ))}
            </div>

            <p className="text-[10px] text-text-secondary mt-3 italic">
              Audio stays local — no recording is sent to a server.
              Browser support: Chrome / Edge ✓ · Safari (limited) · Firefox ✗
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
